import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { adminGet, adminPost, adminPut, adminDel, authHeaders } from "@/lib/adminApi";
import { Volume2, Trash2 } from "lucide-react";
import { PILLAR_LABELS } from "@/lib/instant";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLARS = Object.keys(PILLAR_LABELS);
const STATUSES = ["brouillon", "à valider", "validé", "publié", "archivé"];
const EMPTY = { pillar: "respirer", title: "", description: "", minutes: 5, kind: "texte", status: "brouillon", feelings: [], needs: [], moment: "" };

export default function Contents() {
  const [contents, setContents] = useState([]);
  const [media, setMedia] = useState([]);
  const [feelingsDir, setFeelingsDir] = useState([]); // répertoire Ressentis & besoins (multi-attribution)
  const [pillar, setPillar] = useState(null);
  const [form, setForm] = useState(null);
  const [uploadingSound, setUploadingSound] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [listening, setListening] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteCode, setDeleteCode] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const rowAudioRef = useRef(null);

  // Clic sur la grosse icône : on ENTEND le bruitage (boucle) ; reclic = silence
  const toggleRowSound = (c) => {
    if (playingId === c.id) {
      rowAudioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    rowAudioRef.current?.pause();
    const a = new Audio(`${API}/media/${c.sound_path}`);
    a.loop = true;
    rowAudioRef.current = a;
    a.play().then(() => setPlayingId(c.id)).catch(() => toast("Lecture impossible."));
  };
  const soundInput = useRef(null);
  const mediaInput = useRef(null);
  const audioRef = useRef(null);
  const formRef = useRef(null);

  // Le formulaire s'ouvre SOUS LES YEUX de Julie (sinon il apparaît tout en haut, hors de l'écran)
  useEffect(() => {
    if (form) formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [form]);

  const load = () => adminGet("/contents").then((d) => setContents(d.contents)).catch(() => {});
  const loadMedia = () => adminGet("/media").then((d) => setMedia(d.media)).catch(() => {});
  useEffect(() => {
    load();
    loadMedia();
    adminGet("/feelings").then((d) => setFeelingsDir(d.feelings || [])).catch(() => {});
    return () => {
      audioRef.current?.pause();
      rowAudioRef.current?.pause();
    };
  }, []);

  // Nouveau contenu ouvert : on arrête l'écoute en cours
  useEffect(() => {
    audioRef.current?.pause();
    setListening(false);
  }, [form?.id]);

  // Envoi direct d'un fichier audio depuis la fiche contenu → médiathèque, puis sélectionné ici
  const uploadSound = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingSound(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/admin/media`, data, { headers: authHeaders() });
      await loadMedia();
      setForm((f) => ({ ...f, sound_path: m.storage_path }));
      toast("Bruitages enregistré — il tournera en boucle pendant la séance.");
    } catch (err) {
      toast(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setUploadingSound(false);
    }
  };

  // Envoi direct d'une image ou d'une vidéo depuis la fiche contenu → médiathèque
  const uploadMedia = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingMedia(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: m } = await axios.post(`${API}/admin/media`, data, { headers: authHeaders() });
      await loadMedia();
      setForm((f) => ({ ...f, media_path: m.storage_path }));
      toast("Média enregistré — il sera le support de l'étape pendant la séance.");
    } catch (err) {
      toast(err.response?.data?.detail || "Envoi impossible.");
    } finally {
      setUploadingMedia(false);
    }
  };

  const toggleListen = () => {
    if (listening) {
      audioRef.current?.pause();
      setListening(false);
      return;
    }
    const a = new Audio(`${API}/media/${form.sound_path}`);
    a.loop = true;
    audioRef.current = a;
    a.play().then(() => setListening(true)).catch(() => toast("Lecture impossible."));
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast("Donnez un titre au contenu.");
      return;
    }
    try {
      if (form.id) await adminPut(`/contents/${form.id}`, form);
      else await adminPost("/contents", form);
      toast("Contenu enregistré.");
      setForm(null);
      load();
    } catch {
      toast("Une erreur est survenue.");
    }
  };

  const setStatus = async (c, status) => {
    await adminPut(`/contents/${c.id}`, { ...c, status }).catch(() => {});
    load();
  };

  const archive = async (c) => {
    await adminDel(`/contents/${c.id}`).catch(() => {});
    toast("Contenu archivé.");
    load();
  };

  // Suppression définitive : exige le code secret administrateur (Réglages)
  const confirmDelete = async () => {
    if (!deleteCode.trim()) {
      toast("Saisissez le code secret.");
      return;
    }
    try {
      await adminPost(`/contents/${deleteTarget.id}/supprimer`, { code: deleteCode.trim() });
      toast("Contenu supprimé définitivement.");
      setDeleteTarget(null);
      setDeleteCode("");
      load();
    } catch (err) {
      toast(err.response?.data?.detail || "Suppression refusée.");
    }
  };

  const list = contents.filter((c) => !pillar || c.pillar === pillar);

  return (
    <div data-testid="admin-contents-module">
      <h2 className="rav-admin-h2">Contenus</h2>
      <div className="rav-chips rav-admin-chips-left">
        <button type="button" className={`rav-chip${!pillar ? " is-selected" : ""}`} data-testid="filter-all" onClick={() => setPillar(null)}>
          Tous
        </button>
        {PILLARS.map((p) => (
          <button key={p} type="button" className={`rav-chip${pillar === p ? " is-selected" : ""}`} data-testid={`filter-${p}`} onClick={() => setPillar(p)}>
            {PILLAR_LABELS[p]}
          </button>
        ))}
      </div>
      <button type="button" className="rav-primary-button rav-admin-create" data-testid="content-create" onClick={() => setForm({ ...EMPTY })}>
        + Créer un contenu
      </button>

      {form && (
        <div className="rav-admin-form" data-testid="content-form" ref={formRef}>
          <select className="rav-input" data-testid="content-form-pillar" value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })}>
            {PILLARS.map((p) => (
              <option key={p} value={p}>{PILLAR_LABELS[p]}</option>
            ))}
          </select>
          <input className="rav-input" data-testid="content-form-title" placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <textarea className="rav-textarea" data-testid="content-form-desc" placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="rav-admin-form-row">
            <input className="rav-input" data-testid="content-form-minutes" type="number" min="1" placeholder="Minutes" value={form.minutes} onChange={(e) => setForm({ ...form, minutes: Number(e.target.value) })} />
            <select className="rav-input" data-testid="content-form-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="rav-admin-form-row">
            <select className="rav-input" data-testid="content-form-audience" value={form.audience || "adulte"} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
              <option value="adulte">Public adulte</option>
              <option value="enfant">Public enfant</option>
            </select>
            <select className="rav-input" data-testid="content-form-media" value={form.media_path || ""} onChange={(e) => setForm({ ...form, media_path: e.target.value || null })}>
              <option value="">Aucun média</option>
              {media.map((m) => (
                <option key={m.id} value={m.storage_path}>{m.original_filename}</option>
              ))}
            </select>
            <input
              ref={mediaInput}
              type="file"
              accept="image/*,video/*"
              hidden
              data-testid="content-form-media-input"
              onChange={uploadMedia}
            />
            <button type="button" className="rav-text-link" data-testid="content-form-media-upload" disabled={uploadingMedia} onClick={() => mediaInput.current?.click()}>
              {uploadingMedia ? "Envoi en cours…" : "Envoyer une image ou une vidéo"}
            </button>
          </div>
          <p className="rav-page-sub rav-admin-note" data-testid="content-form-media-hint">
            Le média (voix guidée, vidéo ou image) est le support principal de l'étape : les supports s'enchaînent pendant l'Instant, quel que soit leur format.
          </p>
          <div className="rav-admin-form-row">
            <select className="rav-input" data-testid="content-form-sound" value={form.sound_path || ""} onChange={(e) => setForm({ ...form, sound_path: e.target.value || null })}>
              <option value="">Aucun bruitage</option>
              {media.filter((m) => m.content_type?.startsWith("audio")).map((m) => (
                <option key={m.id} value={m.storage_path}>{m.original_filename}</option>
              ))}
            </select>
            <input
              ref={soundInput}
              type="file"
              accept="audio/mpeg,audio/mp4,audio/wav,audio/ogg,.mp3,.m4a,.wav,.ogg"
              hidden
              data-testid="content-form-sound-input"
              onChange={uploadSound}
            />
            <button type="button" className="rav-text-link" data-testid="content-form-sound-upload" disabled={uploadingSound} onClick={() => soundInput.current?.click()}>
              {uploadingSound ? "Envoi en cours…" : "Envoyer un audio"}
            </button>
            {form.sound_path && (
              <button type="button" className="rav-text-link" data-testid="content-form-sound-listen" onClick={toggleListen}>
                {listening ? "Arrêter l'écoute" : "Écouter"}
              </button>
            )}
            {form.sound_path && (
              <button
                type="button"
                className="rav-text-link rav-note-delete"
                data-testid="content-form-sound-remove"
                onClick={() => {
                  audioRef.current?.pause();
                  setListening(false);
                  setForm({ ...form, sound_path: null });
                  toast("Bruitages retiré — pensez à Enregistrer.");
                }}
              >
                Retirer le bruitage
              </button>
            )}
          </div>
          <p className="rav-page-sub rav-admin-note" data-testid="content-form-sound-hint">
            Le bruitage (fichier audio) tourne en boucle pendant toute la durée de la séance de ce contenu.
          </p>
          <div className="rav-field" data-testid="content-form-feelings">
            <span className="rav-page-sub rav-admin-note" style={{ display: "block", marginBottom: 6 }}>
              Ressentis compatibles — cochez-en PLUSIEURS si le contenu y répond :
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {feelingsDir.map((f) => {
                const on = (form.feelings || []).includes(f.name);
                return (
                  <button
                    key={f.id}
                    type="button"
                    data-testid={`content-feeling-${f.name}`}
                    className={on ? "rav-primary-button" : "rav-secondary-button"}
                    style={{ padding: "4px 12px", fontSize: "0.82rem", width: "auto", minWidth: 0, borderRadius: 999 }}
                    aria-pressed={on}
                    onClick={() =>
                      setForm({
                        ...form,
                        feelings: on ? (form.feelings || []).filter((x) => x !== f.name) : [...(form.feelings || []), f.name],
                      })
                    }
                  >
                    {f.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rav-field" data-testid="content-form-needs">
            <span className="rav-page-sub rav-admin-note" style={{ display: "block", marginBottom: 6 }}>
              Besoins compatibles — cochez-en PLUSIEURS :
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {[...new Set(feelingsDir.flatMap((f) => f.needs || []))].map((n) => {
                const on = (form.needs || []).includes(n);
                return (
                  <button
                    key={n}
                    type="button"
                    data-testid={`content-need-${n}`}
                    className={on ? "rav-primary-button" : "rav-secondary-button"}
                    style={{ padding: "4px 12px", fontSize: "0.82rem", width: "auto", minWidth: 0, borderRadius: 999 }}
                    aria-pressed={on}
                    onClick={() =>
                      setForm({
                        ...form,
                        needs: on ? (form.needs || []).filter((x) => x !== n) : [...(form.needs || []), n],
                      })
                    }
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
          <select className="rav-input" data-testid="content-form-moment" value={form.moment || ""} onChange={(e) => setForm({ ...form, moment: e.target.value })}>
            <option value="">Moment : peu importe</option>
            <option value="matin">Plutôt le matin</option>
            <option value="journée">Plutôt la journée</option>
            <option value="soir">Plutôt le soir</option>
          </select>
          <div className="rav-session-actions">
            <button type="button" className="rav-secondary-button rav-session-btn" data-testid="content-form-save" onClick={save}>
              Enregistrer
            </button>
            <button type="button" className="rav-text-link" data-testid="content-form-cancel" onClick={() => setForm(null)}>
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="rav-admin-list">
        {list.map((c, i) => (
          <div key={c.id} className={`rav-admin-row${c.sound_path ? " has-sound" : ""}`} data-testid={`content-row-${i}`}>
            <span className="rav-admin-row-label">« {c.title} » — {c.minutes} min</span>
            <span className="rav-admin-row-sub">{PILLAR_LABELS[c.pillar]} · {c.status || "publié"} · {c.description}</span>
            <span className="rav-admin-row-actions">
              <button type="button" className="rav-text-link" data-testid={`content-edit-${i}`} onClick={() => setForm({ ...EMPTY, ...c })}>
                Modifier
              </button>
              {c.status !== "publié" && (
                <button type="button" className="rav-text-link" data-testid={`content-publish-${i}`} onClick={() => setStatus(c, "publié")}>
                  Publier
                </button>
              )}
              {c.status !== "archivé" && (
                <button type="button" className="rav-text-link" data-testid={`content-archive-${i}`} onClick={() => archive(c)}>
                  Archiver
                </button>
              )}
              <button
                type="button"
                className="rav-text-link rav-note-delete rav-trash"
                data-testid={`content-delete-${i}`}
                aria-label="Supprimer définitivement ce contenu"
                title="Supprimer définitivement (code secret requis)"
                onClick={() => {
                  setDeleteTarget(c);
                  setDeleteCode("");
                }}
              >
                <Trash2 size={15} strokeWidth={1.6} />
              </button>
            </span>
            {c.sound_path && (
              <button
                type="button"
                className={`rav-sound-flag${playingId === c.id ? " is-playing" : ""}`}
                data-testid={`content-sound-icon-${i}`}
                aria-label={playingId === c.id ? "Arrêter l'écoute du bruitage" : "Écouter le bruitage en boucle"}
                title={playingId === c.id ? "Arrêter l'écoute" : "Écouter le bruitage"}
                onClick={() => toggleRowSound(c)}
              >
                <Volume2 size={30} strokeWidth={1.6} />
              </button>
            )}
          </div>
        ))}
      </div>

      {deleteTarget && (
        <div className="rav-photo-modal" data-testid="content-delete-modal" onClick={() => setDeleteTarget(null)}>
          <div className="rav-photo-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="rav-section-title">Supprimer « {deleteTarget.title} » ?</h2>
            <p className="rav-photo-hint">Cette suppression est définitive. Saisissez le code secret administrateur (défini dans Réglages).</p>
            <input
              className="rav-input"
              type="password"
              inputMode="numeric"
              data-testid="content-delete-code"
              placeholder="Code secret"
              value={deleteCode}
              onChange={(e) => setDeleteCode(e.target.value)}
            />
            <div className="rav-photo-actions">
              <button type="button" className="rav-text-link" data-testid="content-delete-cancel" onClick={() => setDeleteTarget(null)}>
                Annuler
              </button>
              <button type="button" className="rav-primary-button rav-photo-save" data-testid="content-delete-confirm" onClick={confirmDelete}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
